import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SelfQuote() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined); // undefined=loading, null=not found
  const [started, setStarted] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", state: "", product_interest: "", preferred_contact: "Phone" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/public/quote/${slug}`).then(({ data }) => setPage(data)).catch(() => setPage(null));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/quote/${slug}`, f);
      setDone(data);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (page === undefined) return <div className="min-h-screen grid place-items-center bg-[#17324D]"><Loader2 className="h-6 w-6 animate-spin text-white/70" /></div>;
  if (page === null) return <div className="min-h-screen grid place-items-center bg-[#17324D] text-slate-300 p-6 text-center">This quote page isn’t available.</div>;

  return (
    <div className="min-h-screen bg-[#17324D] text-slate-100 flex flex-col" data-testid="self-quote-page">
      <div className="absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#315A7D]/40 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#B28A3D]/15 blur-3xl" />

      <div className="relative mx-auto w-full max-w-lg px-6 py-12 flex-1">
        <div className="gold-rule mb-6" />
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#d9b978]">{page.agency_name}</div>
        <h1 className="mt-2 font-heading text-4xl font-extrabold leading-tight text-white" data-testid="sq-headline">
          {page.agency_name} — Get a Quote
        </h1>
        <p className="mt-4 text-slate-300 text-sm leading-relaxed">{page.intro}</p>

        {done ? (
          <div className="mt-8 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10" data-testid="sq-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <h2 className="mt-3 font-heading text-xl font-bold text-white">Thank you — we’ve got your request.</h2>
            <p className="mt-1 text-sm text-slate-300">A licensed agent from {page.agency_name} will reach out shortly. Reference #{done.reference}.</p>
          </div>
        ) : !started ? (
          <Button size="lg" className="mt-8 bg-[#B28A3D] hover:bg-[#9c7833] text-white" data-testid="sq-start" onClick={() => setStarted(true)}>
            Start Your Quote <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label className="text-slate-200">Full name *</Label><Input required value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-white/90 text-slate-900" data-testid="sq-name" /></div>
              <div><Label className="text-slate-200">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white/90 text-slate-900" data-testid="sq-phone" /></div>
              <div><Label className="text-slate-200">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="bg-white/90 text-slate-900" data-testid="sq-email" /></div>
              <div>
                <Label className="text-slate-200">State</Label>
                <select value={f.state} onChange={(e) => set("state", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="sq-state">
                  <option value="">Select…</option>
                  {(page.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slate-200">Product interest</Label>
                <select value={f.product_interest} onChange={(e) => set("product_interest", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="sq-product">
                  <option value="">Select…</option>
                  {(page.products || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-200">Preferred contact method</Label>
                <select value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} className="mt-1 h-9 w-full rounded-md bg-white/90 px-3 text-sm text-slate-900" data-testid="sq-contact">
                  {["Phone", "Email", "Text"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Provide your name and a phone or email so we can reach you.</p>
            <Button type="submit" disabled={submitting} className="w-full bg-[#B28A3D] hover:bg-[#9c7833] text-white" data-testid="sq-submit">
              {submitting ? "Submitting…" : "Request My Quote"}
            </Button>
          </form>
        )}

        <div className="mt-8 flex items-start gap-2 text-[11px] text-slate-400">
          <ShieldCheck className="h-4 w-4 shrink-0 text-[#B28A3D]" />
          <span>Quote & placement decisioning is powered by InstaBrain where applicable. {page.agency_name} is an independent insurance agency; it does not underwrite or issue insurance. Your information is submitted securely to the agency for follow-up.</span>
        </div>
      </div>

      <div className="relative border-t border-white/10 py-4 text-center text-[11px] text-slate-500">
        Powered by Fortunate Operations
      </div>
    </div>
  );
}
